/**
 * Centralized organization ID utility functions
 * Handles organization ID storage and retrieval without exposing it in URLs
 */

const ORG_ID_KEY = "organizationId";

/**
 * Get the current organization ID from localStorage
 * @returns {string | null} The organization ID or null if not found
 */
export const getOrganizationId = (): string | null => {
  try {
    return localStorage.getItem(ORG_ID_KEY);
  } catch (error) {
    console.error("Error getting organization ID from localStorage:", error);
    return null;
  }
};

/**
 * Set the organization ID in localStorage
 * @param {string} organizationId - The organization ID to store
 */
export const setOrganizationId = (organizationId: string): void => {
  try {
    if (organizationId) {
      localStorage.setItem(ORG_ID_KEY, organizationId);
    }
  } catch (error) {
    console.error("Error setting organization ID in localStorage:", error);
  }
};

/**
 * Remove the organization ID from localStorage
 * Called during logout
 */
export const clearOrganizationId = (): void => {
  try {
    localStorage.removeItem(ORG_ID_KEY);
  } catch (error) {
    console.error("Error clearing organization ID from localStorage:", error);
  }
};

/**
 * Check if an organization ID is currently stored
 * @returns {boolean} True if organization ID exists, false otherwise
 */
export const hasOrganizationId = (): boolean => {
  return getOrganizationId() !== null;
};

/**
 * Get organization ID for API calls
 * @returns {string} The organization ID or empty string if not found
 */
export const getApiOrganizationId = (): string => {
  return getOrganizationId() || "";
};
